import { Routes, Route, BrowserRouter } from "react-router-dom";
import Login from "./components/security/Login";
import Logout from "./components/security/Logout";
import ForgotPassword from "./components/security/ForgotPassword";
import Signup from "./components/security/Signup";
import RequiredAuth from "./components/security/RequiredAuth";
import { getUser } from "./service/api_service";

import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useState } from "react";
import Manual from "./components/manual/Manual";
import ProjectList from "./components/automated/project_list";
import Account from "./components/account/Account";
import Setting from "./components/account/Setting";
import JobDetail from "./components/manual/JobDetail";
import ProjectDetail from "./components/automated/project_detail";
import LoginCallback from "./components/security/LoginCallback";
import { createTheme, CssBaseline, ThemeProvider, useColorScheme } from "@mui/material";
import { MainLayout } from "./components";
import { Constants } from "./utils/constants";
import { isAdminUser } from "./utils/helper";
import Geist from "./fonts/Geist/Geist-VariableFont_wght.ttf";

const createAppTheme = () => {
  return createTheme({
    colorSchemes: {
      light: {
        palette: {
          primary: {
            main: Constants.Colors.light.primary,
            contrastText: Constants.Colors.light.primaryForeground,
          },
          secondary: {
            main: Constants.Colors.light.secondary,
            contrastText: Constants.Colors.light.secondaryForeground,
          },
          error: {
            main: Constants.Colors.light.destructive,
            contrastText: Constants.Colors.light.destructiveForeground,
          },
          background: {
            default: Constants.Colors.light.background,
            paper: Constants.Colors.light.card,
          },
          text: {
            primary: Constants.Colors.light.foreground,
            secondary: Constants.Colors.light.mutedForeground,
          },
        },
      },
      dark: {
        palette: {
          primary: {
            main: Constants.Colors.dark.primary,
            contrastText: Constants.Colors.dark.primaryForeground,
          },
          secondary: {
            main: Constants.Colors.dark.secondary,
            contrastText: Constants.Colors.dark.secondaryForeground,
          },
          error: {
            main: Constants.Colors.dark.destructive,
            contrastText: Constants.Colors.dark.destructiveForeground,
          },
          background: {
            default: Constants.Colors.dark.background,
            paper: Constants.Colors.dark.card,
          },
          text: {
            primary: Constants.Colors.dark.foreground,
            secondary: Constants.Colors.dark.mutedForeground,
          },
        },
      },
    },
    typography: {
      fontFamily: "Geist, Geist Fallback",
      allVariants: {
        fontFamily: "Geist, Geist Fallback",
        textTransform: "none",
      },
      button: {
        textTransform: "none",
        fontFamily: "Geist, Geist Fallback",
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: `
          @font-face {
            font-family: 'Geist';
            src: local('Geist'), url(${Geist}) format('opentype');
          }
        `,
      },
    }
  });
}

function getItem(label, key, icon, children, type) {
  return {
    key,
    icon,
    children,
    label,
    type,
  };
}

const AppContent = observer(({ authStore, jobStore }) => {
  const [wordpress, setWordpress] = useState(false);
  const { setMode } = useColorScheme();

  useEffect(() => {
    const stored = localStorage.getItem("mode");
    if (stored) {
      setMode(stored);
    }
  }, [setMode]);

  const items = [
    getItem("Projects", "automated"),
    getItem("Manual", "manual"),
  ];

  if (authStore.user && authStore.user.org && authStore.user.org.main_user === authStore.user.user_id) {
    items.push(getItem("Account", "account"));
    items.push(getItem("Settings", "setting"));
  } else {
    items.push(getItem("Settings", "setting"));
  }
  items.push(getItem("Logout", "logout"));

  useEffect(() => {
    setWordpress(process.env.REACT_APP_BUILD_TARGET === "wordpress");
  }, []);

  const onOpenChange = (keys) => {
    console.log(keys);
    const key = keys.key || keys;
    if (key === "manual") {
      document.location = "/manual";
    } else if (key === "automated") {
      document.location = "/";
    } else if (key === "account") {
      document.location = "/account";
    } else if (key === "setting") {
      document.location = "/setting";
    } else if (key === "logout") {
      document.location = "/logout";
    }
  };

  const fetchData = useCallback(async () => {
    const user = await getUser();
    authStore.updateUser(user);
  }, [authStore]);

  useEffect(() => {
    if (authStore.session !== null) {
      fetchData();
    }
  }, [authStore.session, fetchData]);

  return (
    <>
      <CssBaseline />
      <MainLayout
        menus={isAdminUser(authStore.user) ? Constants.AdminMenus : Constants.UserMenus}
        onOpenChange={onOpenChange}
        activeMenu={jobStore.navigation}
        isAuth={authStore.authenticated}
      >
        <BrowserRouter>
          <Routes>
            <Route
              path="/manual"
              element={
                <RequiredAuth
                  authStore={authStore}
                  children={
                    <Manual authStore={authStore} jobStore={jobStore} />
                  }
                />
              }
            />
            <Route
              path="/"
              element={
                <RequiredAuth
                  authStore={authStore}
                  children={
                    <ProjectList authStore={authStore} jobStore={jobStore} />
                  }
                />
              }
            />
            <Route
              path="/project/:id"
              element={
                <RequiredAuth
                  authStore={authStore}
                  children={
                    <ProjectDetail
                      authStore={authStore}
                      jobStore={jobStore}
                    />
                  }
                />
              }
            />
            <Route
              path="/account"
              element={
                <RequiredAuth
                  authStore={authStore}
                  children={
                    <Account authStore={authStore} jobStore={jobStore} />
                  }
                />
              }
            />

            <Route
              path="/setting"
              element={
                <RequiredAuth
                  authStore={authStore}
                  children={
                    <Setting authStore={authStore} jobStore={jobStore} />
                  }
                />
              }
            />
            <Route
              path="/job/:id"
              element={
                <RequiredAuth
                  authStore={authStore}
                  children={
                    <JobDetail
                      authStore={authStore}
                      jobStore={jobStore}
                    />
                  }
                />
              }
            />
            <Route
              path="/login-callback"
              element={<LoginCallback authStore={authStore}></LoginCallback>}
            />
            <Route
              path="/login"
              element={<Login authStore={authStore} />}
            />
            <Route
              path="/signup/:option"
              element={<Signup authStore={authStore} />}
            />
            <Route
              path="/logout"
              element={<Logout authStore={authStore} />}
            />
            <Route
              path="/forgot"
              element={<ForgotPassword authStore={authStore} />}
            />
          </Routes>
        </BrowserRouter>
      </MainLayout>
    </>
  );
});

function App({ authStore, jobStore }) {
  const theme = createAppTheme();

  return (
    <ThemeProvider theme={theme} defaultMode="light">
      <AppContent authStore={authStore} jobStore={jobStore} />
    </ThemeProvider>
  );
}

export default observer(App);
